import { create } from 'zustand';
import type { PricingSlot } from '../engine/types';
import { pricing as pricingApi } from '../lib/api';

interface PricingState {
  slots: PricingSlot[];
  load: () => Promise<void>;
  create: (data: Omit<PricingSlot, 'id'>) => Promise<void>;
  update: (id: number, data: Partial<Omit<PricingSlot, 'id'>>) => Promise<void>;
  remove: (id: number) => Promise<void>;
}

export const usePricingStore = create<PricingState>((set) => ({
  slots: [],

  load: async () => {
    const slots = await pricingApi.getAll();
    set({ slots });
  },

  create: async (data) => {
    const slot = await pricingApi.create(data);
    set((s) => ({ slots: [...s.slots, slot] }));
  },

  update: async (id, data) => {
    const updated = await pricingApi.update(id, data);
    set((s) => ({ slots: s.slots.map((slot) => (slot.id === id ? updated : slot)) }));
  },

  remove: async (id) => {
    await pricingApi.delete(id);
    set((s) => ({ slots: s.slots.filter((slot) => slot.id !== id) }));
  },
}));

/**
 * Returns the active price for the given game type based on the current time.
 * Falls back to the provided default if no slot matches.
 */
export function getActivePrice(slots: PricingSlot[], defaultFrame: number, defaultRack: number): { frame: number; rack: number } {
  if (slots.length === 0) return { frame: defaultFrame, rack: defaultRack };

  const now = new Date();
  const current = now.getHours() * 60 + now.getMinutes();

  const sorted = [...slots].sort((a, b) => a.sortOrder - b.sortOrder || a.id - b.id);

  for (const slot of sorted) {
    const [sh, sm] = slot.startTime.split(':').map(Number);
    const [eh, em] = slot.endTime.split(':').map(Number);
    if (isNaN(sh) || isNaN(sm) || isNaN(eh) || isNaN(em)) continue;
    const start = sh * 60 + sm;
    const end = eh * 60 + em;
    // Handle overnight periods (e.g. 22:00 → 06:00)
    const matches = end <= start
      ? current >= start || current < end
      : current >= start && current < end;
    if (matches) return { frame: slot.pricePerFrame, rack: slot.pricePerRack };
  }

  return { frame: defaultFrame, rack: defaultRack };
}
